/**
 * @fileoverview Auth Store (Zustand)
 * 
 * Manages authentication state: role, restaurant, user data.
 * Replaces RoleContext with better performance.
 * 
 * @module stores/useAuthStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { wsService } from '@/services/websocket/WebSocketService';
import { clearCoGerantChats } from '@/utils/coGerantSession';

/**
 * Auth Store - Role & Restaurant State
 * 
 * @example
 * // Use specific selectors for better performance
 * const role = useAuthStore((state) => state.role);
 * const restaurant = useAuthStore((state) => state.restaurant);
 * 
 * // Or use the legacy hook for backward compatibility
 * const { role, restaurant, setRole, clearRole, loading } = useRole();
 */
export const useAuthStore = create(
  persist(
    (set, get) => ({
      // Auth State
      role: '',
      restaurant: null,
      loading: true,
      
      // Initialize from localStorage (called on app start)
      initialize: () => {
        if (typeof window === 'undefined') return;
        
        const userObj = JSON.parse(localStorage.getItem('userObj') || 'null');
        if (userObj) {
          set({
            role: userObj.role_name || '',
            restaurant: {
              id: userObj.restaurant_id,
              name: userObj.restaurant_name,
            },
            loading: false,
          });
        } else {
          set({ loading: false });
        }
      },
      
      // Set role and restaurant (called after login)
      setRole: (userData) => {
        set({
          role: userData.role_name || '',
          restaurant: {
            id: userData.restaurant_id,
            name: userData.restaurant_name,
          },
        });
        // Logged in / switched: realtime rooms waiting for the token join now.
        wsService.authChanged();
      },
      
      // Clear role (called on logout)
      clearRole: () => {
        set({
          role: '',
          restaurant: null,
        });
        localStorage.removeItem('userObj');
        clearCoGerantChats();
        // Logged out: the realtime connection must not keep the user's rooms.
        wsService.disconnect();
        // Note: Navigation should be handled by the component calling clearRole
      },
      
      // Update restaurant data
      setRestaurant: (restaurant) => set({ restaurant }),
      
      // Check if user is authenticated
      isAuthenticated: () => {
        const { role } = get();
        return !!role;
      },
      
      // Check if user has specific role
      hasRole: (requiredRole) => {
        const { role } = get();
        return role === requiredRole;
      },
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        role: state.role,
        restaurant: state.restaurant,
      }),
      onRehydrateStorage: () => (state) => {
        // After rehydration, sync with localStorage userObj
        if (typeof window !== 'undefined') {
          const userObj = JSON.parse(localStorage.getItem('userObj') || 'null');
          if (userObj && state) {
            state.role = userObj.role_name || '';
            state.restaurant = {
              id: userObj.restaurant_id,
              name: userObj.restaurant_name,
            };
          }
          if (state) {
            state.loading = false;
          }
        }
      },
    }
  )
);

// Initialize on first load (client-side only)
if (typeof window !== 'undefined') {
  // Small delay to ensure localStorage is available
  setTimeout(() => {
    useAuthStore.getState().initialize();
  }, 0);
}

/**
 * Legacy hook for backward compatibility with RoleContext
 * Drop-in replacement: import { useRole } from '@/stores';
 */
export const useRole = () => {
  const role = useAuthStore((state) => state.role);
  const restaurant = useAuthStore((state) => state.restaurant);
  const loading = useAuthStore((state) => state.loading);
  const setRole = useAuthStore((state) => state.setRole);
  const clearRole = useAuthStore((state) => state.clearRole);
  
  return { role, restaurant, loading, setRole, clearRole };
};
